/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, mergeMap, catchError } from 'rxjs/operators';
import { Agent, Client, Company, FormStatus, FormView, Submission } from '../models';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ConfigService } from '@xilo-mono/common/config';

export interface ValidateClientResponse {
  success: boolean;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class SubmissionService {
  apiUrl = `${this.configService.apiUrl}/submission`;
  formViewUrl = `${this.configService.apiUrl}/form-view`;
  updateMetadata = new BehaviorSubject<any>({});
  metadata: any = {};
  filesForUpload = [];

  constructor(private http: HttpClient, private configService: ConfigService) {}

  // checkSubmissionFromProspect : check if submission is valid from prospects information
  checkSubmissionFromProspect(submissionId:number, body:{email:string,phone:string,firstName:string,lastName:string}) : Observable<ValidateClientResponse>{
    return this.http.post<ValidateClientResponse>(`${this.configService.apiUrl}/new/client/validate/submission/${submissionId}`,body)
  }

  onUpdateMetadata(updateObj: any, field: FormlyFieldConfig) {
    const parentKey = field.parent?.key;
    if (parentKey !== undefined && (isNaN(+parentKey) || +parentKey === 0)) {
      this.updateMetadata.next(updateObj);
    }
  }

  assignAgent(submissionId: number, clientId: number) {
    return this.http
      .post(`${this.apiUrl}/assign-agent`, { submissionId, clientId })
      .pipe(
        map((result: { title: string; obj: Agent }) => result.obj)
      );
  }

  updateSubmission(submission: Submission) {
    return this.http
      .patch(`${this.apiUrl}/update`, submission)
      .pipe(map((result) => result['obj']));
  }

  deleteSubmission(id: number | string) {
    return this.http
      .delete(`${this.apiUrl}/delete/${id}`)
      .pipe(map((result) => result['obj']));
  }

  getSubmission(id: string | number): Observable<Submission> {
    return this.http
      .get(`${this.apiUrl}/list-one/${id}`)
      .pipe(map((result) => result['obj']));
  }

  getSubmissionTracker(id: string | number): Observable<any> {
    return this.http
      .get(`${this.apiUrl}/${id}/tracker`)
      .pipe(map((result) => result));
  }

  getSubmissionByClient(id: string | number) {
    return this.http
      .get(`${this.apiUrl}/list-clients/${id}`)
      .pipe(map((result) => result['obj']));
  }

  getFormViewBySubmission(id: string | number, companyId: string, version?: number) {
    let url = `${this.formViewUrl}/list-one/${companyId}/${id}`;
    if (version > 0) {
      url += `?version=${version}`;
    }
    return this.http.get(url).pipe(map((result) => result['obj']));
  }

  isClientHasAnySubmissions(id: string | number) {
    return this.http
      .get(`${this.apiUrl}/has-submissions/${id}`)
      .pipe(map((result) => result['obj']));
  }

  getFormAndSubmissionBySubmissionId(id: string | number, companyId: string) {
    return this.getSubmission(id).pipe(
      mergeMap((res) => {
        return combineLatest([
          of(res),
          this.getFormViewBySubmission(
            res.metadata.formId,
            companyId,
            res.metadata.version
          ),
        ]);
      }),
      map(([submission, formView]) => {
        return {
          submission: submission,
          formView: formView,
        };
      }),
      catchError((error) => {
        console.log(error);
        return of(error);
      })
    );
  }

  getByClientWithForm(id: string | number) {
    return this.http
      .get(`${this.apiUrl}/list-by-client/${id}`)
      .pipe(map((result) => result['obj']));
  }

  newCustomerSubmission(
    metadata: any,
    responses?: any,
  ): Observable<Submission> {
    const submission: Submission = {
      responses,
      metadata,
    };
    const url = `${this.apiUrl}/new-customer`;
    return this.http
      .post(url, submission)
      .pipe(map((result: {  obj: Submission  }) => result.obj));
  }

  /**
   * Create secondary customer form submission from client portal
   * @param metadata
   * @param responses
   */
  createSecondaryFormSubmission(
    metadata: any,
    responses?: any,
  ): Observable<Submission> {
    const submission: Submission = { responses, metadata };
    const url = `${this.apiUrl}/create-submission-authenticated`;
    return this.http
      .post(url, submission)
      .pipe(map((result) => result['obj']));
  }

  submitForm(
    clientId: string | number,
    formId: string,
    companyId: string | number,
    submissionId: number,
    agentId: string | number,
    formType: string,
  ): Observable<Submission> {
    const body = {
      companyId,
      clientId,
      submissionId,
      formId,
      agentId,
      formType,
    };
    return this.http
      .post(`${this.apiUrl}/submit-form`, body)
      .pipe(map((result) => result['title']));
  }

  updateFormStatus(clientId: string | number, formStatus: FormStatus) {
    const url = `${this.apiUrl}/form-status/${clientId}`;
    return this.http.put(url, { formStatus });
  }

  uploadFiles(files: Blob[], clientId?: number, token?: string) {
    if (clientId) {
      const url = `${this.configService.apiUrl}/client/upload-document`;
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      let options;
      if (token) {
        options = {
          headers: { 'x-access-token': token },
        };
      }
      return this.http.post(url, formData, options);
    } else {
      this.filesForUpload = this.filesForUpload.concat(files);
      return of({});
    }
  }

  uploadClientFiles(files: Blob[], clientId?: number, quoteId?: string, token?: string) {
    if (clientId) {
      const url = `${this.configService.apiUrl}/client/upload-documents/${clientId}?quoteId=${quoteId}`;
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      let options;
      if (token) {
        options = {
          headers: { 'x-access-token': token },
        };
      }
      return this.http.post(url, formData, options);
    } else {
      this.filesForUpload = this.filesForUpload.concat(files);
      return of({});
    }
  }

  // uploadClientFilesV2 : allows upload files for the document request, params are dynamic
  uploadClientFilesV2(files: Blob[], clientId?: number, paramOpts?: {taskId?:any,companyId:any,token?:any,quoteId?:string}) {
    if (clientId) {
      const url = `${this.configService.apiUrl}/new/client/upload-client-documents/${clientId}`;
      const formData = new FormData();
      files.forEach((file) => {
        const fileName = `${(<any>file).name}`;
        formData.append('files', file, fileName);
      });
      const options:any = {
        params:paramOpts
      }
      return this.http.post(url, formData, options);
    } else {
      this.filesForUpload = this.filesForUpload.concat(files);
      return of({});
    }
  }

  deleteDocument(documentId) {
    return this.http.delete(`${this.configService.apiUrl}/document/delete-one/${documentId}`);
  }
}
